import useMousePosition from "hooks/useMousePosition";

const ARROW_SIZE = 5;
const MARGIN_FROM_CURSOR = 15;
const MARGIN_FOR_BOTTOM = MARGIN_FROM_CURSOR + 10;
const PLACEMENTS = ["top", "right", "bottom", "left"];
const OPPOSITE_PLACEMENTS = {
    top: "bottom",
    right: "left",
    bottom: "top",
    left: "right",
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max));

const getViewportRect = () => ({
    width: document.documentElement.clientWidth || window.innerWidth || document.body.clientWidth,
    height: document.documentElement.clientHeight || window.innerHeight || document.body.clientHeight,
});

const getPlacementOrder = (placement) => [
    placement,
    OPPOSITE_PLACEMENTS[placement],
    ...PLACEMENTS.filter((item) => item !== placement && item !== OPPOSITE_PLACEMENTS[placement]),
];

const getBasePosition = (placement, cursorX, cursorY, refWidth, refHeight) => {
    const refFloorWidth = Math.floor(refWidth / 2);
    const refFloorHeight = Math.floor(refHeight / 2);

    switch (placement) {
        case "top":
            return [cursorX - refFloorWidth, cursorY - refHeight - MARGIN_FROM_CURSOR];
        case "left":
            return [cursorX - refWidth - MARGIN_FROM_CURSOR, cursorY - refFloorHeight];
        case "right":
            return [cursorX + MARGIN_FROM_CURSOR, cursorY - refFloorHeight];
        case "bottom":
            return [cursorX - refFloorWidth, cursorY + MARGIN_FOR_BOTTOM];
        default:
            return [cursorX - refFloorWidth, cursorY - refHeight - MARGIN_FROM_CURSOR];
    }
};

const clampSecondaryAxis = (placement, position, refWidth, refHeight, viewportWidth, viewportHeight) => {
    const [positionX, positionY] = position;

    if (placement === "top" || placement === "bottom") {
        return [clamp(positionX, 0, viewportWidth - refWidth), positionY];
    }

    return [positionX, clamp(positionY, 0, viewportHeight - refHeight)];
};

const getVisibleArea = (position, refWidth, refHeight, viewportWidth, viewportHeight) => {
    const [positionX, positionY] = position;
    const visibleWidth = Math.max(0, Math.min(positionX + refWidth, viewportWidth) - Math.max(positionX, 0));
    const visibleHeight = Math.max(0, Math.min(positionY + refHeight, viewportHeight) - Math.max(positionY, 0));

    return visibleWidth * visibleHeight;
};

const isFullyVisible = (position, refWidth, refHeight, viewportWidth, viewportHeight) => {
    const [positionX, positionY] = position;

    return (
        positionX >= 0 &&
        positionY >= 0 &&
        positionX + refWidth <= viewportWidth &&
        positionY + refHeight <= viewportHeight
    );
};

const getArrowStyle = (cursorX, cursorY, positionX, positionY, refWidth, refHeight) => ({
    "--tooltip-fme-arrow-x": `${clamp(cursorX - positionX - ARROW_SIZE, 0, refWidth - ARROW_SIZE * 2)}px`,
    "--tooltip-fme-arrow-y": `${clamp(cursorY - positionY - ARROW_SIZE, 0, refHeight - ARROW_SIZE * 2)}px`,
});

const useTooltipPosition = (ref, placement) => {
    const { x, y } = useMousePosition();

    const cursorX = x || 0;
    const cursorY = y || 0;
    const initialPlacement = PLACEMENTS.includes(placement) ? placement : "top";

    const refClientRect = ref?.current?.getBoundingClientRect();
    const viewportRect = getViewportRect();

    const refWidth = refClientRect?.width || 0;
    const refHeight = refClientRect?.height || 0;
    const placementOrder = getPlacementOrder(initialPlacement);
    const placementCandidates = placementOrder.map((item) => {
        const position = clampSecondaryAxis(
            item,
            getBasePosition(item, cursorX, cursorY, refWidth, refHeight),
            refWidth,
            refHeight,
            viewportRect.width,
            viewportRect.height,
        );

        return {
            placement: item,
            position,
            visibleArea: getVisibleArea(position, refWidth, refHeight, viewportRect.width, viewportRect.height),
            fullyVisible: isFullyVisible(position, refWidth, refHeight, viewportRect.width, viewportRect.height),
        };
    });

    const calculatedPosition =
        placementCandidates.find((item) => item.fullyVisible) ||
        placementCandidates.reduce((bestCandidate, item) =>
            item.visibleArea > bestCandidate.visibleArea ? item : bestCandidate,
        );
    const [positionX, positionY] = calculatedPosition.position;
    const adjustedPositionX = clamp(positionX, 0, viewportRect.width - refWidth);
    const adjustedPositionY = clamp(positionY, 0, viewportRect.height - refHeight);

    return [
        adjustedPositionX,
        adjustedPositionY,
        calculatedPosition.placement,
        getArrowStyle(cursorX, cursorY, adjustedPositionX, adjustedPositionY, refWidth, refHeight),
    ];
};

export default useTooltipPosition;
